import React, { useState } from 'react';

import {
  IAjuste,
  IContaDoPlano,
  ICruzamentos,
  IResumo,
  ISecao,
  ISituacaoDoFechamento,
  IVerificacao,
  useFechamentoEmpresa,
  useFechamentoSecao,
  useFechamentoSituacao,
} from '../../../../../../hooks/useFechamentoContabilMensal';
import { AcaoDoFechamento, ConfirmacaoDoFechamento } from '../../empresa/fechamento';
import { mesLongo, REGIME } from '../../regras';
import { formatarCnpj } from '../../regrasDaBusca';
import { NOMES_MES, situacaoDaTela, situacaoDoMes, ultimoMesDe } from '../../regrasDaCarteira';
import { textoDoRt } from '../../regrasDoDetalhe';
import { dataEHora, textoDoEvento } from '../../regrasDoFechamento';
import { AbaAjustes } from './AbaAjustes';
import { AbaConstatacoes } from './AbaConstatacoes';
import { AbaCruzamentos } from './AbaCruzamentos';
import { AbaResumo } from './AbaResumo';
import { BOTAO_AZUL, CAIXA, LINK, pilula, ROTULO } from './estilos';

export type AbaDoDetalhe = 'resumo' | 'constatacoes' | 'cruzamentos' | 'ajustes';
const ABAS: { chave: AbaDoDetalhe; nome: string }[] = [
  { chave: 'resumo', nome: 'Resumo' },
  { chave: 'constatacoes', nome: 'Constatações' },
  { chave: 'cruzamentos', nome: 'Cruzamentos' },
  { chave: 'ajustes', nome: 'Ajustes propostos' },
];

const ABREV = NOMES_MES.map(n => n.slice(0, 3).toLowerCase());

const mesAnterior = (competencia: string) => {
  const [a, m] = competencia.split('-').map(Number);
  return m === 1 ? `${a - 1}-12` : `${a}-${String(m - 1).padStart(2, '0')}`;
};

const NOTA: React.CSSProperties = {
  padding: '10px 12px',
  borderLeft: '3px solid #1B6FD0',
  borderRadius: 8,
  background: 'rgba(241, 245, 249, .9)',
  fontSize: 12,
  color: '#334155',
};

function Aviso({ children }: { children: React.ReactNode }) {
  return (
    <p
      style={{
        margin: 0,
        padding: 22,
        textAlign: 'center',
        fontSize: 13,
        color: '#52525B',
        background: 'rgba(248, 250, 252, .8)',
        borderRadius: 12,
      }}
    >
      {children}
    </p>
  );
}

/** Seção que não veio: carregando, sem revisão ou versão sem detalhe. */
export function Pendente({
  secao,
  nome,
}: {
  secao: { isLoading: boolean; data?: ISecao<unknown> };
  nome: string;
}) {
  if (secao.isLoading) return <Aviso>Carregando {nome}...</Aviso>;
  if (secao.data && !secao.data.disponivel)
    return (
      <Aviso>
        Detalhe indisponível para a versão {secao.data.versao}. {secao.data.motivo}
      </Aviso>
    );
  return <Aviso>Sem {nome} para esta competência.</Aviso>;
}

/** Situação da competência e as ações de pessoa: fechar e reabrir. */
export function StatusDaCompetencia({
  situacao,
  empresaId,
  competencia,
}: {
  situacao: ISituacaoDoFechamento;
  empresaId: number;
  competencia: string;
}) {
  const [acao, setAcao] = useState<AcaoDoFechamento | null>(null);
  const [verHistorico, setVerHistorico] = useState(false);
  const fechada = situacao.situacao === 'FECHADO';
  const pelaMaquina = fechada && situacao.fechamento?.origem === 'sistema';
  const ultimo = situacao.historico[situacao.historico.length - 1];
  const sistema = situacao.sistema_proprio ?? null;
  const bloqueio = fechada ? situacao.bloqueio_reabrir : situacao.bloqueio_fechar;

  let rotulo = 'Provisória';
  if (fechada) rotulo = pelaMaquina ? 'Fechada pelo sistema' : 'Fechada pela área';
  let texto =
    'Ainda não foi fechada. Enquanto estiver provisória, o processamento atualiza a revisão quando os dados mudam.';
  if (fechada && situacao.fechamento) texto = textoDoEvento(situacao.fechamento);
  else if (!fechada && ultimo) texto = textoDoEvento(ultimo);

  return (
    <div style={{ ...CAIXA, gap: 10, padding: '14px 16px' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 10px' }}>
        <span
          style={{
            ...pilula(fechada ? '#E5F5ED' : '#EEF2F6', fechada ? '#15803D' : '#475569'),
            borderRadius: 6,
          }}
        >
          {rotulo}
        </span>
        {fechada && situacao.fechamento?.com_pendencia && (
          <span
            style={{ ...pilula('#FCF1DD', '#B45309'), borderRadius: 6 }}
            title="Encerrada no Domínio com pendência aberta na revisão"
          >
            Fechada com pendência
          </span>
        )}
        {sistema && (
          <span style={{ ...pilula('#E9F4FD', '#155FB0'), borderRadius: 6 }}>Sistema próprio</span>
        )}
        <span style={{ flex: '1 1 300px', minWidth: 0, fontSize: 12.5, color: '#3F3F46' }}>
          {texto}
          {situacao.historico.length > 1 && (
            <>
              {' '}
              <button
                type="button"
                onClick={() => setVerHistorico(v => !v)}
                style={{ ...LINK, fontSize: 12 }}
              >
                {verHistorico ? 'ocultar histórico' : `histórico (${situacao.historico.length})`}
              </button>
            </>
          )}
        </span>
        {fechada ? (
          <button
            type="button"
            disabled={!!situacao.bloqueio_reabrir}
            title={situacao.bloqueio_reabrir ?? undefined}
            onClick={() => setAcao('reabrir')}
            style={{
              ...BOTAO_AZUL,
              background: '#FFFFFF',
              color: '#0F172A',
              border: '1px solid #CBD5E1',
              cursor: situacao.bloqueio_reabrir ? 'not-allowed' : 'pointer',
              opacity: situacao.bloqueio_reabrir ? 0.45 : 1,
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#0F172A"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="4" y="11" width="16" height="10" rx="2" />
              <path d="M8 11V7a4 4 0 0 1 7.5-2" />
            </svg>
            Reabrir competência
          </button>
        ) : (
          <button
            type="button"
            disabled={!!situacao.bloqueio_fechar}
            title={situacao.bloqueio_fechar ?? undefined}
            onClick={() => setAcao('fechar')}
            style={{
              ...BOTAO_AZUL,
              cursor: situacao.bloqueio_fechar ? 'not-allowed' : 'pointer',
              opacity: situacao.bloqueio_fechar ? 0.45 : 1,
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#FFFFFF"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="4" y="11" width="16" height="10" rx="2" />
              <path d="M8 11V7a4 4 0 0 1 8 0v4" />
            </svg>
            Fechar competência
          </button>
        )}
      </div>
      {bloqueio && <span style={{ fontSize: 12, color: '#B45309' }}>{bloqueio}</span>}
      {verHistorico && (
        <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12, color: '#52525B' }}>
          {situacao.historico.map(e => (
            <li key={`${e.versao}-${e.evento}-${e.em}`}>
              {textoDoEvento(e)}
              {e.versao != null && e.versao !== situacao.versao && ` (versão ${e.versao})`}
            </li>
          ))}
        </ul>
      )}
      {sistema && (
        <div style={NOTA}>
          <b>Fechamento em sistema próprio ({sistema}).</b> A contabilidade desta empresa é fechada
          no sistema do cliente. Aqui o fechamento é só controle.
        </div>
      )}
      {situacao.regra_anterior && (
        <div style={{ ...NOTA, borderLeftColor: '#D97706' }}>
          <b>Calculada com a regra anterior.</b> O sistema recebeu uma correção depois desta
          apuração e vai atualizá-la na próxima revisão da carteira. Até lá, alguns números podem
          não refletir a correção.
        </div>
      )}
      {situacao.alterada_em && (
        <div style={{ ...NOTA, borderLeftColor: '#DC2626' }}>
          <b>Alterada depois de fechada.</b> Em {dataEHora(situacao.alterada_em)} o processamento
          encontrou dados diferentes dos usados neste fechamento. Os números continuam sendo os do
          fechamento. Para revisar com os dados atuais, reabra a competência.
        </div>
      )}
      <ConfirmacaoDoFechamento
        acao={acao}
        onSair={() => setAcao(null)}
        situacao={situacao}
        empresaId={empresaId}
        competencia={competencia}
      />
    </div>
  );
}

/**
 * Detalhe de uma empresa marcada nos cards, na competência do card. Lê só a
 * revisão gravada: cada aba busca a sua seção quando é aberta.
 */
export function DetalheDaEmpresa({
  empresaId,
  competencia,
  razao,
  aba,
  onAba,
}: {
  empresaId: number;
  competencia: string;
  /** do card, enquanto a revisão carrega */
  razao: string;
  aba: AbaDoDetalhe;
  onAba: (a: AbaDoDetalhe) => void;
}) {
  const ano = Number(competencia.slice(0, 4));
  const hoje = new Date();
  const resumo = useFechamentoSecao<IResumo>('resumo', empresaId, competencia);
  const anterior = useFechamentoSecao<IResumo>(
    'resumo',
    empresaId,
    mesAnterior(competencia),
    aba === 'resumo',
  );
  const situacao = useFechamentoSituacao(empresaId, competencia, !!resumo.data);
  const { data: doAno } = useFechamentoEmpresa(empresaId, `${ano}-01`, `${ano}-12`);
  const verificacoes = useFechamentoSecao<IVerificacao[]>(
    'verificacoes',
    empresaId,
    competencia,
    aba === 'constatacoes',
  );
  const plano = useFechamentoSecao<Record<string, IContaDoPlano>>(
    'plano',
    empresaId,
    competencia,
    aba === 'constatacoes' || aba === 'cruzamentos',
  );
  // a contagem da aba Cruzamentos é a dos divergentes: a seção vem com o detalhe
  const cruzamentos = useFechamentoSecao<ICruzamentos>('cruzamentos', empresaId, competencia);
  const ajustes = useFechamentoSecao<IAjuste[]>(
    'ajustes',
    empresaId,
    competencia,
    aba === 'ajustes',
  );

  const r = resumo.data?.dados;
  const empresa = r?.empresa;
  const linha = doAno?.empresa;
  const regime = empresa?.regime_texto ?? REGIME[empresa?.regime ?? ''];
  const info = [
    linha?.external_id,
    empresa?.cnpj ? `CNPJ ${formatarCnpj(empresa.cnpj)}` : null,
    regime,
    mesLongo(competencia),
    resumo.data ? `versão ${resumo.data.versao}` : null,
    textoDoRt(empresa?.rt),
  ]
    .filter(Boolean)
    .join(' · ');

  const ultimo = ultimoMesDe(ano, hoje);
  const porMes = new Map((linha?.meses ?? []).map(m => [m.competencia, m]));
  const divergentes = (cruzamentos.data?.dados?.cruzamentos ?? []).filter(
    c => c.situacao === 'divergente',
  ).length;
  const contagem: Partial<Record<AbaDoDetalhe, number>> = {
    constatacoes: r ? r.contagem.critica + r.contagem.alta : 0,
    cruzamentos: divergentes,
    ajustes: r?.ajustes ?? 0,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ ...ROTULO, letterSpacing: '.08em' }}>Fechamento contábil mensal</div>
        <h2
          style={{
            margin: '2px 0',
            fontSize: 22,
            fontWeight: 800,
            letterSpacing: '-.01em',
            lineHeight: 1.15,
            textTransform: 'uppercase',
          }}
        >
          {empresa?.razao ?? razao}
        </h2>
        <div style={{ fontSize: 12, color: '#52525B' }}>{info}</div>
      </div>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '10px 12px',
          padding: '10px 14px',
          borderRadius: 12,
          border: '1px solid #E4E4E7',
          background: 'rgba(255, 255, 255, .6)',
        }}
      >
        <span style={ROTULO}>Competência</span>
        <span style={{ fontSize: 13, fontWeight: 700 }}>{ano}</span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {ABREV.slice(0, Math.max(0, ultimo + 1)).map((rot, i) => {
            const comp = `${ano}-${String(i + 1).padStart(2, '0')}`;
            const m = porMes.get(comp);
            const s = m?.tem_detalhe ? situacaoDaTela(situacaoDoMes(m)) : null;
            return (
              <span
                key={comp}
                title={`${NOMES_MES[i]} de ${ano}: ${s ? s.curto.toLowerCase() : 'sem revisão'}`}
                style={{
                  minWidth: 40,
                  boxSizing: 'border-box',
                  padding: '5px 8px',
                  borderRadius: 8,
                  textAlign: 'center',
                  fontSize: 11.5,
                  fontWeight: 700,
                  background: s?.suave ?? '#F1F5F9',
                  color: s?.texto ?? '#64748B',
                  boxShadow: comp === competencia ? '0 0 0 2px #1B6FD0' : 'none',
                }}
              >
                {rot}
              </span>
            );
          })}
        </div>
      </div>

      {situacao.data ? (
        <StatusDaCompetencia
          situacao={situacao.data}
          empresaId={empresaId}
          competencia={competencia}
        />
      ) : null}

      <div
        role="tablist"
        aria-label="Seções da empresa"
        style={{ display: 'flex', flexWrap: 'wrap', gap: 2, borderBottom: '1px solid #E4E4E7' }}
      >
        {ABAS.map(a => {
          const ativo = a.chave === aba;
          const n = contagem[a.chave] ?? 0;
          return (
            <button
              type="button"
              key={a.chave}
              role="tab"
              aria-selected={ativo}
              onClick={() => onAba(a.chave)}
              style={{
                padding: '10px 12px',
                border: 0,
                background: 'transparent',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: ativo ? 700 : 500,
                color: ativo ? '#0F172A' : '#52525B',
                boxShadow: `inset 0 -2px 0 ${ativo ? '#1B6FD0' : 'transparent'}`,
              }}
            >
              {a.nome}
              {n ? ` (${n})` : ''}
            </button>
          );
        })}
      </div>

      {aba === 'resumo' &&
        (r && resumo.data?.disponivel ? (
          <AbaResumo
            resumo={r}
            anterior={anterior.data?.disponivel ? anterior.data.dados : undefined}
            competencia={competencia}
            onVerTodas={() => onAba('constatacoes')}
          />
        ) : (
          <Pendente secao={resumo} nome="a revisão" />
        ))}
      {aba === 'constatacoes' &&
        (verificacoes.data?.dados ? (
          <AbaConstatacoes itens={verificacoes.data.dados} plano={plano.data?.dados} />
        ) : (
          <Pendente secao={verificacoes} nome="constatações" />
        ))}
      {aba === 'cruzamentos' &&
        (cruzamentos.data?.dados ? (
          <AbaCruzamentos
            dados={cruzamentos.data.dados}
            materialidade={r?.materialidade}
            plano={plano.data?.dados}
          />
        ) : (
          <Pendente secao={cruzamentos} nome="cruzamentos" />
        ))}
      {aba === 'ajustes' &&
        (ajustes.data?.dados ? (
          <AbaAjustes ajustes={ajustes.data.dados} lucroDoMes={r?.dre.mes.lucro_liquido} />
        ) : (
          <Pendente secao={ajustes} nome="ajustes" />
        ))}
    </div>
  );
}
